import { WorkPage } from '../components/work-page.tsx';
import './schedule-pages.css';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { scheduleApi } from '../api/endpoints.ts';
import { Alert, Button, LoadingBlock } from '../components/ui.tsx';
import { useDocumentTitle } from '../hooks/index.ts';
import { PlanRequiredNotice, usePlanFeature } from '../components/plan-gate.tsx';

/**
 * The page a notification opens when a customer asked to take an open spot
 * and the office has to approve the move.
 *
 * Earlier-visit offers are on every paid plan and never on Free; a Free
 * company opening an old notification meets the plan notice, not an error.
 */
export default function OpenSpotPage() {
  const { offerId = '' } = useParams();
  const { t } = useTranslation();
  useDocumentTitle(t('openSpot.documentTitle'));
  const queryClient = useQueryClient();
  const queryKey = ['slot-offer', offerId];

  const allowed = usePlanFeature('earlier_visit_offers');
  const query = useQuery({
    queryKey,
    queryFn: () => scheduleApi.slotOffer(offerId),
    enabled: allowed && offerId.length > 0,
  });

  const approve = useMutation({
    mutationFn: () => scheduleApi.approveSlotOffer(offerId),
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  });
  const decline = useMutation({
    mutationFn: () => scheduleApi.declineSlotOffer(offerId),
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  });

  const offer = query.data;
  const pending = offer?.status === 'pending_approval';
  const busy = approve.isPending || decline.isPending;

  return (
    <WorkPage title={t('openSpot.heading')}>
      {!allowed ? <PlanRequiredNotice feature="earlier_visit_offers" /> : null}
      {query.isLoading ? <LoadingBlock label={t('openSpot.loading')} /> : null}
      {allowed && (query.isError || (!query.isLoading && !offer)) ? (
        <Alert tone="warning">{t('openSpot.unavailable')}</Alert>
      ) : null}
      {offer ? (
        <section className="card">
          <p className="text-lg font-semibold">{t('openSpot.when', { day: offer.day, time: offer.time })}</p>
          {offer.customerName ? <p className="mt-2">{t('openSpot.customer', { name: offer.customerName })}</p> : null}
          {offer.status === 'filled' ? <p className="mt-4">{t('openSpot.filled')}</p> : null}
          {offer.status === 'declined' ? <p className="mt-4">{t('openSpot.declined')}</p> : null}
          {offer.status !== 'pending_approval' && offer.status !== 'filled' && offer.status !== 'declined' ? (
            <p className="mt-4">{t('openSpot.closed')}</p>
          ) : null}
          {pending ? (
            <div className="mt-6 flex flex-wrap gap-3">
              <Button variant="primary" busy={approve.isPending} disabled={busy} onClick={() => approve.mutate()}>
                {t('openSpot.approve')}
              </Button>
              <Button variant="secondary" busy={decline.isPending} disabled={busy} onClick={() => decline.mutate()}>
                {t('openSpot.decline')}
              </Button>
            </div>
          ) : null}
          {approve.isError || decline.isError ? (
            <div className="mt-4">
              <Alert tone="warning">{t('openSpot.unavailable')}</Alert>
            </div>
          ) : null}
        </section>
      ) : null}
    </WorkPage>
  );
}
